import { useActions, useCountriesStore } from "app/store";
import { useI18n } from "features/translation/context";
import { useUserData } from "features/userData/context";
import { _ } from "i18n";
import { hasPermissionToResource } from "libs/authorization";
import client from "libs/client";
import { isGranted } from "pages/productManagement/functions";
import { Permission, Resource } from "pages/productManagement/types";
import { Fragment } from "react";
import { Link, NavLink, useNavigate } from "react-router";
import { MenuLink } from "types";
import { Header, SelectedCountry, useToast } from "ui-kit";
import AccountOptions from "ui-kit/src/Header/AccountOptions/AccountOptions";
import LangSwitch from "ui-kit/src/Header/LangSwitch/LangSwitch";
import { VideoNotifications } from "features/videoUploads/tables/VideoNotifications";

interface AdminHeaderProps {
	user: { name: string; email: string; photo: string | null };
}

export function AdminHeader({ user }: AdminHeaderProps) {
	const { changeLocale, locale } = useI18n();
	const navigate = useNavigate();
	const { addSuccessToast, addErrorToast } = useToast();
	const { selection, countryData } = useCountriesStore();
	const { openCountrySelect } = useActions();
	const { productManagementOptions } = useUserData();

	const userData = useUserData();
	const isUserCM = hasPermissionToResource(userData, "game_uploads", "read");

	const subMenuOptions = {
		subMenuName: _("resource.product_management"),
		options: Object.entries(productManagementOptions ?? []).map(([key, value]) => {
			return {
				label: value.name,
				id: value.subMenuItems.length > 0 ? value.subMenuItems[0].id : value.id,
				link: MenuLink.PRODUCT_MANAGEMENT,
			};
		}),
	};

	const getNavItems = () => {
		const navItems = [
			{
				id: "clubs-amateur",
				resource: Resource.GAME_UPLOADS,
				permission: Permission.READ,
				label: _("header.admin.upload_video"),
				link: "/discover/v2/games",
			},
			{
				id: "game-uploads",
				resource: Resource.GAME_UPLOADS,
				permission: Permission.READ,
				label: "Video server",
				link: "/video-uploads/not-sent",
			},
			{
				id: "clubs-pro",
				resource: Resource.CLUBS_PRO,
				permission: Permission.LIST,
				label: _("header.admin.club_pro"),
				link: "/admin/clubs-pro",
			},
			{
				id: "product-management",
				resource: Resource.PRODUCT_MANAGEMENT,
				permission: Permission.READ,
				label: "Product administration",
				link: "product-management",
			},
			{
				id: "operations-management",
				resource: Resource.OPERATIONS_MANAGEMENT,
				permission: Permission.READ,
				label: "Operations management",
				link: "operations-management/summary",
			},
		];

		return navItems.filter((config) => isGranted(config.resource, config.permission));
	};

	const mutation = client.adminJsonLogout.useMutation({
		onSuccess: () => {
			addSuccessToast(_("logoff.success"));
			navigate("/login");
		},
		onError: () => {
			addErrorToast(_("logoff.fail"));
		},
	});

	const onLogout = () => {
		mutation.mutate();
		navigate("/login");
	};

	return (
		<>
			<Header
				logo={(logo) => (
					<Link to="/">
						<img className="header__logo-img" src={logo} alt="logo" />
					</Link>
				)}
				mainItems={getNavItems().map((item, index) => (
					<NavLink to={item.link} className="header__link" key={`tab-${index}`}>
						{item.label}
					</NavLink>
				))}
				rightItems={[
					<SelectedCountry onClick={openCountrySelect} selection={selection} countryData={countryData} key="country-selection" />,
					isUserCM ? <VideoNotifications key="video-notifications" /> : <Fragment key="video-notifications" />,
					<LangSwitch onChange={changeLocale} selectedLocale={locale} key="lang-switch" />,
					<AccountOptions
						navItems={getNavItems()}
						subMenuOptions={subMenuOptions}
						user={user}
						onLogout={onLogout}
						key="account-options"
						apiUrl=""
					/>,
				]}
			/>
		</>
	);
}
